/**
 * Client per l'API di verifica titoli (ticket-verifier-api >= v1.2.1).
 *
 * Centralizza la chiamata, la gestione degli stati HTTP di errore e il parsing
 * del validation_log, che prima erano duplicati in scanner.tsx e insertSeriale.tsx.
 * Non solleva mai eccezioni: in caso di problema restituisce una risposta con
 * `success: false` e `errorMessage` gia' pronto per essere mostrato all'utente.
 */

/** Formato accettato dall'API per il seriale inserito a mano (es. "2-00001234"). */
export const SERIAL_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;

export interface TicketResponse {
  success: boolean;
  error?: string | null;
  status?: number | null;
  formatted_status?: string | null;
  fare_description_extended?: string | null;
  serial_number?: string | null;
  residual_trip?: number | null;
  end_validity_date_time?: string | null;
  validity_end_date_time?: string | null;
  validation_log?: string | null;
  /** Data della prima convalida, ricavata dal validation_log. */
  firstValidationDate?: string | null;
  /** Valorizzato solo se la verifica non e' andata a buon fine. */
  errorMessage?: string;
}

const GENERIC_ERROR =
  "Non è stato possibile completare la verifica. Riprova tra qualche istante.";

/** Traduce lo stato HTTP in un messaggio comprensibile all'utente. */
function messageForStatus(httpStatus: number): string {
  switch (httpStatus) {
    case 429:
      return "Troppe verifiche in poco tempo. Attendi un minuto e riprova.";
    case 422:
      return "Il codice inserito non è in un formato valido. Controllalo e riprova.";
    case 401:
    case 403:
      return GENERIC_ERROR;
    default:
      return GENERIC_ERROR;
  }
}

/** Estrae la data della prima convalida dal validation_log, se presente. */
function extractFirstValidationDate(validationLog?: string | null): string | null {
  if (!validationLog) return null;
  try {
    const parsed = JSON.parse(validationLog);
    if (!Array.isArray(parsed)) return null;
    const first = parsed
      .filter((log: any) => log?.ValidationCode === 40)
      .sort(
        (a: any, b: any) =>
          new Date(a.EventDT).getTime() - new Date(b.EventDT).getTime()
      )[0];
    return first ? new Date(first.EventDT).toLocaleDateString("it-IT") : null;
  } catch (err) {
    console.error("validation_log non interpretabile:", err);
    return null;
  }
}

export interface VerifyTicketParams {
  barcodeData?: string;
  cryptogramSerialNumber?: string;
}

export async function verifyTicket(
  params: VerifyTicketParams
): Promise<TicketResponse> {
  const apiUrl = process.env.REACT_APP_API_URL || "/verifierapi";

  // L'API accetta esattamente uno dei due identificativi valorizzati.
  const body = {
    barcodeData: params.barcodeData ?? "",
    cryptogramSerialNumber: params.cryptogramSerialNumber ?? "",
  };

  let response: Response;
  try {
    response = await fetch(`${apiUrl}/verify-ticket`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Token": process.env.REACT_APP_TOKEN_API || "",
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    console.error("Errore di rete nella verifica del titolo:", err);
    return {
      success: false,
      errorMessage:
        "Impossibile contattare il servizio. Controlla la connessione e riprova.",
    };
  }

  if (!response.ok) {
    console.error(`Verifica fallita, HTTP ${response.status}`);
    return { success: false, errorMessage: messageForStatus(response.status) };
  }

  let data: TicketResponse;
  try {
    data = await response.json();
  } catch (err) {
    console.error("Risposta non interpretabile come JSON:", err);
    return { success: false, errorMessage: GENERIC_ERROR };
  }

  if (!data.success) {
    return { ...data, success: false, errorMessage: GENERIC_ERROR };
  }

  return { ...data, firstValidationDate: extractFirstValidationDate(data.validation_log) };
}

/** Formatta una data ISO per l'utente; restituisce null se assente o non valida. */
export function formatDateTime(
  value?: string | null,
  locale?: string
): string | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toLocaleString(locale);
}

/** true se la data e' valida e non ancora passata. Una data assente vale false. */
export function isFuture(value?: string | null): boolean {
  if (!value) return false;
  const d = new Date(value);
  return !isNaN(d.getTime()) && d >= new Date();
}
